import type { HTMLAttributes, ReactNode } from 'react';

/*
 * `title` wird aus den HTML-Attributen ausgeschlossen: dort ist es die
 * Kurzinfo beim Zeigen mit der Maus (ein String), hier die Ueberschrift der
 * Karte (beliebiger Inhalt).
 */
export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Ueberschrift. Ohne sie bleibt der Kopfbereich weg. */
  title?: ReactNode;
  /** Erklaerender Satz unter der Ueberschrift. */
  description?: ReactNode;
  /** Knoepfe oben rechts. */
  actions?: ReactNode;
  /** Rand hervorheben — fuer Warnungen und Fehlerfaelle. */
  tone?: 'default' | 'warning' | 'danger';
  /** Innenabstand weglassen, wenn der Inhalt selbst einer traegt (Tabellen). */
  flush?: boolean;
  children: ReactNode;
}

/**
 * Abgegrenzte Flaeche.
 *
 * Die Ueberschrift wird als h2 ausgegeben. Das ist bewusst festgelegt und
 * nicht einstellbar: Karten stehen in dieser Anwendung immer unterhalb der
 * einen Seitenueberschrift, und eine frei waehlbare Ebene fuehrt zuverlaessig
 * zu Dokumenten, in denen die Gliederung springt.
 */
export function Card({
  title,
  description,
  actions,
  tone = 'default',
  flush = false,
  children,
  className,
  ...rest
}: CardProps) {
  return (
    <section
      {...rest}
      className={`card ${className ?? ''}`.trim()}
      data-tone={tone === 'default' ? undefined : tone}
    >
      {(title || actions) && (
        <header className="card__head">
          <div className="card__heading">
            {title && <h2 className="card__title">{title}</h2>}
            {description && <p className="card__description">{description}</p>}
          </div>
          {actions && <div className="card__actions">{actions}</div>}
        </header>
      )}
      <div className="card__body" data-flush={flush || undefined}>
        {children}
      </div>
    </section>
  );
}
